<script>
  // Owns the whole attachment picker. Each chosen file gets a description, so
  // the selection cannot live in one multi-file input: it is rendered back into
  // the form as an indexed attachment_uploads[i][file]/[description] pair per
  // file, which is the shape AttachmentUploadable reads. Because those inputs
  // are really in the form, both Turbo and a plain submit carry them.

  // prefix is the form field prefix, e.g. "attachment_uploads".
  let { prefix = "attachment_uploads" } = $props()

  const MAX_SIZE = 20 * 1024 * 1024

  const ALLOWED_TYPES = [
    "application/pdf",
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/gif",
    "image/webp",
  ]

  // Browsers disagree on the MIME type of the office formats, so an
  // allow-listed extension is accepted even when the type is not recognised.
  const ALLOWED_EXTENSIONS = [
    "pdf", "txt", "md", "csv", "xls", "xlsx", "doc", "docx",
    "png", "jpg", "jpeg", "gif", "webp",
  ]

  const ACCEPT = ALLOWED_TYPES.join(",")

  let picker = $state()
  let dragging = $state(false)
  let errors = $state([])
  let nextId = 0
  let picked = $state([])

  function extensionOf(fileName) {
    return fileName.split(".").pop().toLowerCase()
  }

  function rejection(file) {
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
      return "File type not supported"
    }
    if (file.size > MAX_SIZE) return `File too large (max ${formatSize(MAX_SIZE)})`
    return null
  }

  function add(files) {
    const rejected = []

    for (const file of files) {
      const reason = rejection(file)
      if (reason) rejected.push(`${file.name}: ${reason}`)
      else picked.push({ id: nextId++, file, description: "" })
    }

    errors = rejected
  }

  function remove(id) {
    const index = picked.findIndex((entry) => entry.id === id)
    if (index !== -1) picked.splice(index, 1)
  }

  function handleChange(event) {
    add(event.target.files)
    // Let the same file be chosen again after it is removed.
    picker.value = ""
  }

  function handleDrop(event) {
    event.preventDefault()
    dragging = false
    add(event.dataTransfer.files)
  }

  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  function iconKind(fileName) {
    const extension = extensionOf(fileName)
    if (extension === "pdf") return "pdf"
    if (["doc", "docx"].includes(extension)) return "doc"
    if (["xls", "xlsx", "csv"].includes(extension)) return "sheet"
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(extension)) return "image"
    return "file"
  }

  // A file input's contents cannot be set from markup, so each carried file is
  // written onto its own input as a one-item DataTransfer.
  function carry(node, file) {
    const transfer = new DataTransfer()
    transfer.items.add(file)
    node.files = transfer.files
  }
</script>

<!-- Drag and drop is a pointer-only shortcut; the button inside carries the
     keyboard and assistive-technology path, so the box itself has no role. -->
<div
  class="dropzone"
  class:is-dragging={dragging}
  role="presentation"
  ondragover={(event) => {
    event.preventDefault()
    dragging = true
  }}
  ondragleave={() => (dragging = false)}
  ondrop={handleDrop}
>
  <button type="button" class="dropzone-open" onclick={() => picker.click()}>
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
    </svg>
    <span>Drop files here or tap to select</span>
    <span class="is-size-7 has-text-grey">PDF, Word, Excel, images up to 20MB</span>
  </button>

  {#if picked.length > 0}
    <div class="attachment-file-list">
      {#each picked as entry, index (entry.id)}
        <div class="file-row">
          <span class="icon has-text-grey">
            {#if iconKind(entry.file.name) === "pdf"}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            {:else if iconKind(entry.file.name) === "doc"}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            {:else if iconKind(entry.file.name) === "sheet"}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <rect x="8" y="12" width="8" height="6"></rect>
              </svg>
            {:else if iconKind(entry.file.name) === "image"}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            {:else}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            {/if}
          </span>

          <div class="file-meta">
            <p class="is-size-7 has-text-weight-semibold">{entry.file.name}</p>
            <p class="is-size-7 has-text-grey">{formatSize(entry.file.size)}</p>
          </div>

          <input
            type="text"
            class="input is-small"
            placeholder="Description (optional)"
            aria-label="Description for {entry.file.name}"
            bind:value={entry.description}
          />

          <button
            type="button"
            class="delete"
            aria-label="Remove {entry.file.name}"
            onclick={() => remove(entry.id)}
          ></button>

          <input type="file" name="{prefix}[{index}][file]" use:carry={entry.file} hidden />
          <input type="hidden" name="{prefix}[{index}][description]" value={entry.description} />
        </div>
      {/each}
    </div>
  {/if}
</div>

{#if errors.length > 0}
  <div class="notification is-danger mt-3">
    {#each errors as error}
      <p>{error}</p>
    {/each}
  </div>
{/if}

<input
  bind:this={picker}
  type="file"
  multiple
  accept={ACCEPT}
  onchange={handleChange}
  class="is-sr-only"
  data-testid="attachments-input"
/>

<style>
  .dropzone {
    border: 2px dashed var(--bulma-border);
    border-radius: var(--bulma-radius-large);
    padding: 1rem;
    transition:
      border-color 150ms ease-out,
      background-color 150ms ease-out;
  }

  .dropzone.is-dragging {
    border-color: var(--bulma-link);
    background-color: var(--bulma-scheme-main-bis);
  }

  /* The whole empty area is the target, so the affordance is a full-width
     button rather than a click handler on the box itself. */
  .dropzone-open {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    padding: 1rem 0;
    border: 0;
    background: none;
    color: var(--bulma-text);
    cursor: pointer;
  }

  .attachment-file-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .file-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: var(--bulma-radius);
    background-color: var(--bulma-scheme-main-bis);
  }

  .file-meta {
    flex: 1 1 8rem;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .file-row .input {
    flex: 1 1 10rem;
    min-width: 0;
  }
</style>
